
import Vue from 'vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins/VuexFirestoreBindingsMixin';

type LiveDocBindState = 'loading' | 'bound' | 'failed';
export type LiveDocState = 'loading' | 'live' | 'removed' | 'static';

/**
 * One row of a Postgres-backed table, live. Postgres picked the row; this binds
 * the Firestore document it names and hands its slot the document to render,
 * plus where the binding has got to:
 *
 *   <live-doc :doc-path="`teams/${row.id}`" :stub="teamStub(row)" v-slot="{ doc, state }">
 *     <team-row :curr-team="doc" />
 *   </live-doc>
 *
 * Until the document arrives (and if its binding fails) the slot gets `stub`,
 * built from the Postgres row in the document's own shape, so a row never waits
 * on Firestore to show. A document that is gone reads `removed`, once, as an
 * event too: the table asks Postgres again after the mirror's lag. An edit never
 * removes a row: one that no longer fits the list's filter or sort stays where it
 * is, live, until the list is fetched again.
 *
 * Renders nothing of its own: the slot's root is the root, which keeps a table
 * row a direct child of the table body. Boards has its own (RunnitBoardLiveDoc),
 * which builds its stand-in itself.
 */
export default Vue.extend({
	name: 'LiveDoc',
	props: {
		docPath: { type: String, required: true },
		stub: { type: Object, default: null },
	},
	data () {
		return {
			liveDoc: null as Record<string, unknown> | null,
			bindState: 'loading' as LiveDocBindState,
		};
	},
	computed: {
		state (): LiveDocState {
			if (this.bindState === 'loading') return 'loading';
			if (this.bindState === 'failed') return 'static';
			if (!this.liveDoc) return 'removed';
			return 'live';
		},
		// The newest copy there is: the live document once it has arrived, else the stand-in.
		doc (): Record<string, unknown> | null {
			return this.liveDoc || this.stub;
		},
	},
	watch: {
		docPath: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal !== oldVal) this.bindDoc(newVal);
			},
		},
		state (newVal: LiveDocState, oldVal: LiveDocState) {
			// Once, on the way in: the list asks Postgres again after a mirror lag.
			if (newVal === 'removed' && oldVal !== 'removed') this.$emit('removed', this.docPath);
		},
	},
	methods: {
		async bindDoc (docPath: string) {
			this.bindState = 'loading';
			if (!docPath) {
				if (this.$firestoreRefs && this.$firestoreRefs.liveDoc) this.$unbind('liveDoc');
				this.liveDoc = null;
				return;
			}
			try {
				await this.$bind('liveDoc', db.doc(docPath), get$bindFirestoreOptions());
				// A newer path took this binding over while this one resolved.
				if (this.docPath === docPath) this.bindState = 'bound';
			} catch (error) {
				console.error(`Error binding ${docPath}:`, error);
				if (this.docPath === docPath) this.bindState = 'failed';
			}
		},
	},
	render () {
		const nodes = this.$scopedSlots.default
			? this.$scopedSlots.default({ doc: this.doc, state: this.state })
			: null;
		return Array.isArray(nodes) ? nodes[0] : nodes;
	},
});
